import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import { isNil } from 'ramda'
import max from 'lodash/max'
import min from 'lodash/min'
import { useMap, type LngLatBoundsLike } from 'react-map-gl/maplibre'

import { useCityData, useNeighbourhoodData } from '@/lib/locations'
import { isEmptyOrNull } from '@/utils'
import { getUrlSlugs } from '@/utils/location'

type Props = {
  zoomLevel: number
  zoomLevelNeighborhood: number
  onLocationChange: () => boolean
}

const CANADA_BOUNDS = [
  [-126.88818661043646, 35.32016689712147],
  [-71.78076473543706, 72.1559413589888]
] as LngLatBoundsLike

export const CityListener = ({
  zoomLevel,
  zoomLevelNeighborhood,
  onLocationChange
}: Props) => {
  const router = useRouter()
  const { neighbourhoodSlug, citySlug } = getUrlSlugs(router)
  const { data: neighborhoodData } = useNeighbourhoodData(
    neighbourhoodSlug,
    citySlug
  )
  const { data: cityData } = useCityData(citySlug)

  const isNeighborhood = !isEmptyOrNull(neighborhoodData)
  const zoom = isNeighborhood ? zoomLevelNeighborhood : zoomLevel

  const { current: map } = useMap()

  const hasFlownTo = useRef<string | undefined>()
  const hasFlownToBox = useRef(false)
  // Prevent crash on initial load using Edge browser:
  const hasFlownToDefault = useRef<boolean | null>()

  const filteredCities = useMemo(() => {
    if (isNeighborhood) {
      return [neighborhoodData]
    } else if (!isEmptyOrNull(cityData)) {
      return [cityData]
    } else {
      return []
    }
  }, [cityData, isNeighborhood, neighborhoodData])

  const firstCity = filteredCities[0]

  // Parse box param from URL (e.g., box=-123.4,45.1,-122.3,46.2)
  const boxParam = router.query.box as string | undefined
  const boxBounds: LngLatBoundsLike | undefined = useMemo(() => {
    if (!boxParam) return undefined
    const parts = boxParam.split(',').map(Number)
    if (parts.length !== 4 || parts.some(isNaN)) return undefined
    const [west, south, east, north] = parts
    return [
      [west, south],
      [east, north]
    ] as LngLatBoundsLike
  }, [boxParam])

  // Track when city/neighbourhood changes
  useEffect(() => {
    hasFlownTo.current = undefined
    hasFlownToBox.current = false
  }, [citySlug, neighbourhoodSlug])

  // Fly to box bounds from URL param (highest priority)
  useLayoutEffect(() => {
    if (!map || !boxBounds || hasFlownToBox.current) return
    map.fitBounds(boxBounds, { duration: 1000, animate: true })
    hasFlownToBox.current = true
  }, [boxBounds, map])

  // Fit bounds for generic Canada page (when no box param)
  useLayoutEffect(() => {
    if (!map || boxBounds) return

    if (filteredCities.length === 1) {
      hasFlownToDefault.current = false
      return
    }

    if (!filteredCities.length) {
      if (hasFlownToDefault.current) return
      hasFlownToDefault.current = true

      map.fitBounds(CANADA_BOUNDS, { animate: true, duration: 300, minZoom: 2 })
    } else {
      const latList = filteredCities.map(city => city.location.lat)
      const lonList = filteredCities.map(city => city.location.lon)
      const southWest = { lng: min(lonList), lat: max(latList) }
      const northEast = { lng: max(lonList), lat: min(latList) }

      map.fitBounds(
        [southWest.lng, southWest.lat, northEast.lng, northEast.lat],
        { duration: 1000 }
      )
      hasFlownTo.current = undefined
    }
  }, [boxBounds, filteredCities, map])

  // Fly to single city or neighborhood (when no box param)
  useLayoutEffect(() => {
    if (!map || boxBounds) return
    if (filteredCities.length !== 1) return
    if (hasFlownTo.current === firstCity.slug) return
    if (isNil(firstCity?.location?.lat) || isNil(firstCity?.location?.lon))
      return

    hasFlownTo.current = firstCity.slug
    map.flyTo({
      center: { lng: firstCity.location.lon, lat: firstCity.location.lat },
      essential: false,
      zoom: zoom,
      animate: true,
      duration: 1000
    })
    onLocationChange()
  }, [boxBounds, filteredCities, firstCity, map, zoom, onLocationChange])

  return null
}
